import React from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  Avatar,
  Divider,
  Stack,
  Chip,
  Paper,
  Slide,
  Grid,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import {
  Close as CloseIcon,
  Phone as PhoneIcon,
  ContentCopy as ContentCopyIcon,
  LocationOn as LocationOnIcon,
  ArrowForward as ArrowForwardIcon,
  WhatsApp as WhatsAppIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import moment from "moment";
import {
  IDataTableV2MobileColumn,
  IDataTableV2MasterColumn,
} from "../../interfaces/IDataTableV2Props";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface IMobileRowDetailDialogProps {
  open: boolean;
  onClose: () => void;
  row: any;
  mobileColumns?: IDataTableV2MobileColumn[];
  masterColumns?: IDataTableV2MasterColumn[];
  onSeeAllDetails?: (row: any) => void;
  onCopyPhone?: (phone: string) => void;
  onWhatsappClick?: (row: any) => void;
  uniqueRowKeyName?: string;
}

export const MobileRowDetailDialog: React.FC<IMobileRowDetailDialogProps> = ({
  open,
  onClose,
  row,
  mobileColumns,
  masterColumns,
  onSeeAllDetails,
  onCopyPhone,
  onWhatsappClick,
}) => {
  const [moreAnchorEl, setMoreAnchorEl] = React.useState<null | HTMLElement>(
    null
  );
  const isMoreOpen = Boolean(moreAnchorEl);

  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMoreAnchorEl(event.currentTarget);
  };

  const handleMoreClose = () => {
    setMoreAnchorEl(null);
  };

  if (!row) return null;

  const firstCol =
    mobileColumns && mobileColumns.length > 0 ? mobileColumns[0] : undefined;

  // Extract Heading Name
  let guestName: React.ReactNode = "";
  if (firstCol?.headingColumn?.onRowCellRender) {
    guestName = (firstCol.headingColumn.onRowCellRender(
      {
        value: row[firstCol.headingColumn.fieldName],
        column: {
          key: firstCol.headingColumn.key,
          headerName: "",
          fieldName: firstCol.headingColumn.fieldName,
          columnIndex: 0,
          rowIndex: 0,
        },
      },
      row
    ) as React.ReactNode) ?? "";
  } else if (firstCol?.headingColumn?.onRowCellValueRender) {
    guestName = firstCol.headingColumn.onRowCellValueRender(
      row[firstCol.headingColumn.fieldName],
      row
    );
  } else if (
    firstCol?.headingColumn?.fieldName &&
    row[firstCol.headingColumn.fieldName]
  ) {
    guestName = row[firstCol.headingColumn.fieldName];
  } else {
    guestName =
      row.name_of_guest ||
      row.name ||
      row.title ||
      row.customer_name ||
      row.event_name ||
      row.booking_name ||
      row.party_name ||
      row.client_name ||
      row.full_name ||
      row.employee_name ||
      (masterColumns && masterColumns.length > 0
        ? row[masterColumns[0].fieldName]
        : null) ||
      "Details";
  }

  const guestNameString =
    typeof guestName === "string"
      ? guestName
      : String(
        row.name_of_guest ||
        row.name ||
        row.title ||
        row.customer_name ||
        "Details"
      );

  // Extract Status
  let statusContent: React.ReactNode = null;
  if (firstCol?.rightHeadingColumn?.onRowCellRender) {
    statusContent = (firstCol.rightHeadingColumn.onRowCellRender(
      {
        value: row[firstCol.rightHeadingColumn.fieldName],
        column: {
          key: firstCol.rightHeadingColumn.key,
          headerName: "",
          fieldName: firstCol.rightHeadingColumn.fieldName,
          columnIndex: 0,
          rowIndex: 0,
        },
      },
      row
    ) as React.ReactNode) ?? null;
  } else if (firstCol?.rightHeadingColumn?.onRowCellValueRender) {
    statusContent = firstCol.rightHeadingColumn.onRowCellValueRender(
      row[firstCol.rightHeadingColumn.fieldName],
      row
    );
  } else {
    const statusVal = row.status || row.actual_status || row.state || row.stage;
    if (statusVal) {
      const sUpper = String(statusVal).toUpperCase();
      statusContent = (
        <Chip
          label={sUpper}
          size="small"
          sx={{
            height: 22,
            fontSize: "0.7rem",
            fontWeight: 700,
            bgcolor:
              sUpper === "BOOKED" || sUpper === "COMPLETED" || sUpper === "APPROVED"
                ? "#dcfce7"
                : sUpper === "CANCELLED" || sUpper === "DEAD" || sUpper === "REJECTED"
                  ? "#fee2e2"
                  : "#e0f2fe",
            color:
              sUpper === "BOOKED" || sUpper === "COMPLETED" || sUpper === "APPROVED"
                ? "#15803d"
                : sUpper === "CANCELLED" || sUpper === "DEAD" || sUpper === "REJECTED"
                  ? "#b91c1c"
                  : "#0369a1",
            borderRadius: "6px",
          }}
        />
      );
    }
  }

  const priority = row.priority || "";
  const contactNo =
    row.contact_number_one ||
    row.phone ||
    row.mobile ||
    row.contact_number ||
    row.phone_number;
  const countryCode = row.country_code || "";
  const fullContact = contactNo ? `${countryCode} ${contactNo}`.trim() : null;

  const preferredVenue = row.preferred_venue || row.venue || "";
  const eventType =
    row.other_event_name ||
    row.event_name ||
    row.event_type ||
    row.type ||
    "";
  const eventDate = row.function_date
    ? moment(row.function_date).format("DD/MM/YYYY")
    : "";
  const followupDate = row.followup_date
    ? moment(row.followup_date).format("DD/MM/YYYY")
    : "";
  const assignedTo =
    row.handled_by_employee || row.assigned_to || row.employee_name || "";
  const guestCount =
    row.number_of_guests || row.pax || row.guest_count || row.capacity || null;
  const leadSource = row.source || row.lead_source_name || "";
  const createdDate = row.create_ts
    ? moment(row.create_ts).format("DD/MM/YYYY")
    : "";
  const remark = row.remarks || row.remark || row.notes || row.comments || "";

  const columnActions = firstCol?.actions || [];

  const handleCopy = () => {
    if (contactNo && onCopyPhone) {
      onCopyPhone(contactNo);
    } else if (contactNo) {
      navigator.clipboard?.writeText(contactNo);
    }
  };

  // Filter hidden actions and remove WhatsApp from floating actions row (since WhatsApp is in contact card above)
  const validActions = columnActions.filter((action) => {
    if (typeof action.hide === "function" && action.hide(row)) {
      return false;
    }
    const nameLower = action.name?.toLowerCase() || "";
    if (nameLower.includes("whatsapp")) {
      return false;
    }
    return true;
  });

  // Separate delete actions (which always go in the menu dropdown) from normal actions
  const deleteActions = validActions.filter((action) =>
    action.name?.toLowerCase().includes("delete")
  );
  const normalActions = validActions.filter(
    (action) => !action.name?.toLowerCase().includes("delete")
  );

  // Show normal actions on screen in one line (up to 6 normal icons)
  const MAX_VISIBLE_ICONS = 6;
  const overflowNormal = normalActions.length > MAX_VISIBLE_ICONS;

  const visibleActions = overflowNormal
    ? normalActions.slice(0, MAX_VISIBLE_ICONS)
    : normalActions;

  const overflowActions = overflowNormal
    ? normalActions.slice(MAX_VISIBLE_ICONS)
    : [];

  // Delete icon ALWAYS goes in menuitem dropdown along with any excess normal actions
  const menuActions = [...overflowActions, ...deleteActions];
  const showMoreMenu = menuActions.length > 0;

  // Card details list items
  const detailsItems = firstCol?.detailsItems
    ? firstCol.detailsItems(row)
    : [
      { label: "Preferred Venue", value: preferredVenue || "--" },
      { label: "Event Type", value: eventType || "--" },
      { label: "Event Date", value: eventDate || "--" },
      { label: "Follow-up Date", value: followupDate || "--" },
      ...(assignedTo ? [{ label: "Assigned To", value: assignedTo }] : []),
      ...(guestCount ? [{ label: "Guests (Pax)", value: `${guestCount}` }] : []),
      ...(leadSource ? [{ label: "Lead Source", value: leadSource }] : []),
      ...(createdDate ? [{ label: "Created Date", value: createdDate }] : []),
    ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          backgroundColor: "transparent",
          boxShadow: "none",
          overflowY: "auto",
          maxHeight: "92vh",
          m: { xs: 1, sm: 2 },
          width: "calc(100% - 16px)",
          maxWidth: "400px",
          // Hide scrollbar for clean aesthetic
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            transition: "all 0.3s ease",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          position: "relative",
          py: 0.5,
        }}
      >
        {/* Detail Card */}
        <Paper
          elevation={12}
          sx={{
            width: "100%",
            borderRadius: "20px",
            backgroundColor: "#ffffff",
            overflow: "hidden",
            p: { xs: 2, sm: 2.5 },
            boxShadow:
              "0 20px 40px -10px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)",
          }}
        >
          {/* Header Section */}
          <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
            <Avatar
              src={row.avatar || row.image || row.avatar_url || undefined}
              sx={{
                width: 48,
                height: 48,
                bgcolor: "#0284c7",
                color: "#ffffff",
                fontSize: "1.2rem",
                fontWeight: 700,
                boxShadow: "0 4px 12px rgba(2, 132, 199, 0.3)",
              }}
            >
              {guestNameString ? guestNameString.charAt(0).toUpperCase() : "G"}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  color: "#0f172a",
                  fontSize: "1.05rem",
                  lineHeight: 1.2,
                  wordBreak: "break-word",
                }}
              >
                {guestName}
              </Typography>

              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                mt={0.5}
                flexWrap="wrap"
              >
                {statusContent}
                {priority && (
                  <Chip
                    label={priority}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      bgcolor:
                        priority.toUpperCase() === "HOT" ||
                          priority.toUpperCase() === "HIGH"
                          ? "#ffe4e6"
                          : priority.toUpperCase() === "WARM"
                            ? "#fef3c7"
                            : "#f1f5f9",
                      color:
                        priority.toUpperCase() === "HOT" ||
                          priority.toUpperCase() === "HIGH"
                          ? "#e11d48"
                          : priority.toUpperCase() === "WARM"
                            ? "#d97706"
                            : "#64748b",
                      borderRadius: "6px",
                    }}
                  />
                )}
                {preferredVenue && (
                  <Chip
                    icon={
                      <LocationOnIcon
                        sx={{
                          fontSize: "14px!important",
                          color: "#0369a1!important",
                        }}
                      />
                    }
                    label={preferredVenue}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      bgcolor: "#e0f2fe",
                      color: "#0369a1",
                      borderRadius: "6px",
                    }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>

          <Divider sx={{ borderColor: "#f1f5f9", my: 1.25 }} />

          {/* Details Body Grid */}
          <Stack spacing={1.25} sx={{ mb: 2 }}>
            {/* Contact Row with WhatsApp & Call Actions (Copy removed) */}
            {contactNo && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1,
                  px: 1.25,
                  borderRadius: "12px",
                  bgcolor: "#f8fafc",
                  border: "1px solid #f1f5f9",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  <PhoneIcon sx={{ color: "#0284c7", fontSize: "18px" }} />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ color: "#1e293b", fontSize: "0.875rem" }}
                  >
                    {fullContact}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <IconButton
                    size="small"
                    onClick={() => {
                      onClose();
                      if (onWhatsappClick) {
                        onWhatsappClick(row);
                      } else if (contactNo) {
                        window.open(
                          `https://wa.me/${contactNo.replace(/[^0-9]/g, "")}`,
                          "_blank"
                        );
                      }
                    }}
                    sx={{
                      bgcolor: "#25D366",
                      color: "#fff",
                      p: 0.5,
                      "&:hover": { bgcolor: "#128C7E" },
                    }}
                    title="WhatsApp Message"
                  >
                    <WhatsAppIcon sx={{ fontSize: "16px" }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    component="a"
                    href={`tel:${contactNo}`}
                    sx={{
                      bgcolor: "#22c55e",
                      color: "#fff",
                      p: 0.5,
                      "&:hover": { bgcolor: "#16a34a" },
                    }}
                    title="Call"
                  >
                    <PhoneIcon sx={{ fontSize: "16px" }} />
                  </IconButton>
                </Stack>
              </Box>
            )}

            {/* Lead Details Grid Cards (2 columns) */}
            <Grid container spacing={1}>
              {detailsItems.map((item: any, idx: number) => (
                <Grid item xs={item.fullWidth ? 12 : 6} key={item.label || idx}>
                  <Box
                    sx={{
                      p: 1,
                      px: 1.25,
                      borderRadius: "12px",
                      bgcolor: "#f8fafc",
                      border: "1px solid #f1f5f9",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#94a3b8",
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        display: "block",
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{
                        color: "#0f172a",
                        mt: 0.2,
                        fontSize: "0.825rem",
                        wordBreak: "break-word",
                      }}
                    >
                      {item.value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Remark / Notes Snippet if present */}
            {remark && (
              <Box
                sx={{
                  p: 1,
                  px: 1.25,
                  borderRadius: "12px",
                  bgcolor: "#f8fafc",
                  border: "1px solid #f1f5f9",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#94a3b8",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    display: "block",
                  }}
                >
                  Remarks / Notes
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#334155",
                    mt: 0.2,
                    fontSize: "0.8rem",
                    fontWeight: 500,
                    lineHeight: 1.35,
                  }}
                >
                  {remark}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* "See all details" Button at Bottom of Card */}
          <Button
            variant="outlined"
            fullWidth
            size="medium"
            endIcon={<ArrowForwardIcon sx={{ fontSize: "18px!important" }} />}
            onClick={() => {
              onClose();
              if (onSeeAllDetails) {
                onSeeAllDetails(row);
              }
            }}
            sx={{
              borderRadius: "20px",
              py: 0.75,
              height: 40,
              fontSize: "0.85rem",
              fontWeight: 700,
              textTransform: "none",
              borderColor: "#cbd5e1",
              color: "#0f172a",
              "&:hover": {
                borderColor: "#0284c7",
                bgcolor: "#f0f9ff",
                color: "#0284c7",
              },
            }}
          >
            See all details
          </Button>
        </Paper>

        {/* Single Horizontal Row of Floating Action Icons Below Card */}
        <Box
          sx={{
            mt: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 0.75, sm: 1.25 },
            flexWrap: "nowrap",
            maxWidth: "100%",
            py: 0.5,
            px: 0.5,
          }}
        >
          {visibleActions.map((action, idx) => (
            <Tooltip title={action.name} key={action.name || idx}>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                  const fn = action.click || (action as any).onClick;
                  if (fn) fn(row);
                }}
                sx={{
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  borderRadius: "50%",
                  bgcolor: "#ffffff",
                  color: action.name?.toLowerCase().includes("delete")
                    ? "#ef4444"
                    : "#1e293b",
                  boxShadow:
                    "0 8px 20px -2px rgba(0, 0, 0, 0.25), 0 2px 6px -1px rgba(0, 0, 0, 0.15)",
                  flexShrink: 0,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: "#f8fafc",
                    transform: "scale(1.08)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
              >
                {React.isValidElement(action.icon)
                  ? React.cloneElement(action.icon as React.ReactElement<any>, {
                    sx: { fontSize: { xs: "18px", sm: "20px" } },
                  })
                  : action.icon}
              </IconButton>
            </Tooltip>
          ))}

          {/* 3-Dot Vertical Menu Button if > 5 actions total */}
          {showMoreMenu && (
            <>
              <Tooltip title="More Options">
                <IconButton
                  onClick={handleMoreClick}
                  sx={{
                    width: { xs: 40, sm: 44 },
                    height: { xs: 40, sm: 44 },
                    borderRadius: "50%",
                    bgcolor: "#ffffff",
                    color: "#1e293b",
                    boxShadow:
                      "0 8px 20px -2px rgba(0, 0, 0, 0.25), 0 2px 6px -1px rgba(0, 0, 0, 0.15)",
                    flexShrink: 0,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      bgcolor: "#f8fafc",
                      transform: "scale(1.08)",
                    },
                  }}
                >
                  <MoreVertIcon sx={{ fontSize: { xs: "18px", sm: "20px" } }} />
                </IconButton>
              </Tooltip>

              <Menu
                anchorEl={moreAnchorEl}
                open={isMoreOpen}
                onClose={handleMoreClose}
                PaperProps={{
                  sx: {
                    borderRadius: "16px",
                    mt: 1,
                    minWidth: 170,
                    boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                  },
                }}
              >
                {menuActions.map((action, idx) => (
                  <MenuItem
                    key={action.name || idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMoreClose();
                      onClose();
                      const fn = action.click || (action as any).onClick;
                      if (fn) fn(row);
                    }}
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      gap: 1.5,
                      py: 1,
                      color: action.name?.toLowerCase().includes("delete")
                        ? "error.main"
                        : "text.primary",
                    }}
                  >
                    {React.isValidElement(action.icon)
                      ? React.cloneElement(action.icon as React.ReactElement<any>, {
                        sx: { fontSize: "18px" },
                      })
                      : action.icon}
                    <Typography variant="body2" fontWeight={600}>
                      {action.name}
                    </Typography>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>

        {/* Circular Close (X) Button at Bottom Center Under Icons */}
        <Box sx={{ mt: 1.25, mb: 0.5, display: "flex", justifyContent: "center" }}>
          <Tooltip title="Close">
            <IconButton
              onClick={onClose}
              sx={{
                width: { xs: 40, sm: 44 },
                height: { xs: 40, sm: 44 },
                borderRadius: "50%",
                bgcolor: "#ffffff",
                color: "#1e293b",
                boxShadow:
                  "0 8px 20px -2px rgba(0, 0, 0, 0.25), 0 2px 6px -1px rgba(0, 0, 0, 0.15)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: "#f8fafc",
                  transform: "scale(1.08)",
                },
                "&:active": {
                  transform: "scale(0.95)",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: { xs: "18px", sm: "22px" } }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Dialog>
  );
};
